import type { RunFailureReason } from "@reagent/sim";
import { TUTORIAL_STAGE } from "@reagent/content";
import { payoutFor } from "@reagent/meta";
import { useGameStore } from "../../app/gameStore";
import { useRunStore } from "../../app/runStore";
import "./run-end.css";

/**
 * The end of a run, which the player has to be able to read at a glance:
 * quota met, or which of the four failures took the rig out. The reason comes
 * off the snapshot rather than being inferred from the board, so the screen
 * always agrees with the event that actually closed the run.
 */
const FAILURE_COPY: Record<RunFailureReason, { title: string; detail: string }> = {
  explosion: {
    title: "Reactor lost",
    detail: "A node sat at critical temperature for two ticks and went up. Cool it, or shed heat.",
  },
  burst: {
    title: "Line burst",
    detail: "Pressure reached 3 on a blocked line. Give the backlog somewhere to go.",
  },
  leak: {
    title: "Line eaten through",
    detail: "Corrosive reagent wore a pipe to 0 HP and it started dumping half its volume a tick.",
  },
  quotaMissed: {
    title: "Out of turns",
    detail: "The quota wasn't met inside the turn budget.",
  },
};

export function RunEnd() {
  const snapshot = useGameStore((s) => s.snapshot);
  const reset = useGameStore((s) => s.reset);
  const mode = useGameStore((s) => s.mode);
  const run = useRunStore((s) => s.run);
  const completeStage = useRunStore((s) => s.completeStage);

  if (!snapshot.isRunComplete && !snapshot.isRunFailed) return null;
  // In a run, once the batch is banked the shop or the run-over screen takes
  // over — this card is only the moment between finishing and finding out.
  if (mode === "run" && run.status !== "inProgress") return null;
  // The induction ends with its own last panel, which hands the player over to
  // a real shift. A modal on top of that would bury the one button that
  // matters under a scorecard for a batch that was never worth points, and
  // offer "run it again" as the obvious next thing to do.
  if (snapshot.stageId === TUTORIAL_STAGE.id) return null;

  const cleared = snapshot.isRunComplete;
  const failure = snapshot.failureReason ? FAILURE_COPY[snapshot.failureReason] : null;

  const tallies = snapshot.tallies;
  const payout = payoutFor(tallies, cleared);
  const target = sum(snapshot.objective.targetComposition);
  const collected = sum(snapshot.objective.collected);

  return (
    <div className="run-end">
      <div className={`run-end__card${cleared ? " is-cleared" : " is-failed"}`}>
        <div className="run-end__stamp">{cleared ? "batch accepted" : "batch scrapped"}</div>
        <h1 className="run-end__title">{cleared ? "Quota met" : (failure?.title ?? "Run failed")}</h1>
        {!cleared && failure && <p className="run-end__detail">{failure.detail}</p>}

        <dl className="run-end__stats">
          <div>
            <dt>yield</dt>
            <dd>
              {collected} / {target}
            </dd>
          </div>
          <div>
            <dt>purity</dt>
            <dd>{tallies.averagePurity}%</dd>
          </div>
          <div>
            <dt>scrap</dt>
            <dd>{tallies.wastedUnits}u</dd>
          </div>
          <div>
            <dt>turns</dt>
            <dd>
              {snapshot.turn} / {snapshot.maxTurns}
            </dd>
          </div>
        </dl>

        {/* The number M5's between-stages budget is earned from, so it is worth
            showing where it came from rather than as a bare total. */}
        <dl className="run-end__payout">
          <div>
            <dt>yield</dt>
            <dd>{payout.yieldCredits}₵</dd>
          </div>
          <div>
            <dt>purity</dt>
            <dd>{payout.purityCredits}₵</dd>
          </div>
          <div>
            <dt>speed</dt>
            <dd>{payout.speedCredits}₵</dd>
          </div>
          <div>
            <dt>valorized</dt>
            <dd>{payout.valorizationCredits}₵</dd>
          </div>
          <div>
            <dt>fine</dt>
            <dd>−{payout.dumpingFine}₵</dd>
          </div>
          <div>
            <dt>vent fine</dt>
            <dd>−{payout.ventFine}₵</dd>
          </div>
        </dl>

        <div className="run-end__score">
          <span className="run-end__score-label">batch payout</span>
          <span className="run-end__score-total">{payout.total}₵</span>
          <span className="run-end__score-note">{describe(snapshot.objective.collected)}</span>
        </div>

        {mode === "run" ? (
          <button
            className="run-end__again"
            onClick={() => completeStage(tallies, cleared, snapshot.deckCards, snapshot.setupSpentCredits)}
          >
            {cleared ? "bank the batch" : "see how the run ended"}
          </button>
        ) : (
          <button className="run-end__again" onClick={reset}>
            run it again
          </button>
        )}
      </div>
    </div>
  );
}

function sum(composition: Record<string, number>): number {
  return Object.values(composition).reduce((total, n) => total + n, 0);
}

function describe(composition: Record<string, number>): string {
  const parts = Object.entries(composition)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([species, units]) => `${units}u ${species}`);
  return parts.length > 0 ? parts.join(", ") : "nothing collected";
}
